import React from "react";
import { Link } from "react-router-dom";
import LandingLayout from "../components/LandingLayout";
import { useRedirectIfLoggedIn } from "../hooks/useRedirectIfLoggedIn";
import LandingHero from "../components/landing/LandingHero";
import ProblemsSection from "../components/landing/ProblemsSection";
import SolutionGrid from "../components/landing/SolutionGrid";
import EarlyAccessCTA from "../components/landing/EarlyAccessCTA";
import FinalCTASection from "../components/landing/FinalCTASection";

const problems = [
    {
        title: "No sabes si reformar compensa",
        description: "Invertir en una reforma puede sumar miles de euros al valor de venta. O puede no compensar. Sin datos, es una apuesta.",
    },
    {
        title: "Presupuestos lentos y difíciles",
        description: "Conseguir 2-3 presupuestos de constructor lleva semanas. Y luego comparar es imposible porque cada uno presupuesta diferente.",
    },
    {
        title: "Miedo a vender mal",
        description: "Sin conocer el valor real de mercado, puedes infravalorarte (dejar dinero en la mesa) o sobreestimarte (y no vender).",
    },
];

const solutions = [
    {
        icon: "📈",
        title: "Valor actual de mercado",
        description: "Basado en datos reales de transacciones y anuncios de tu zona. No una opinión, un dato.",
    },
    {
        icon: "💰",
        title: "Coste estimado de reforma",
        description: "Desglosado por partidas con precios de constructores reales de tu zona. En minutos, no en semanas.",
    },
    {
        icon: "🎯",
        title: "Valor post-reforma",
        description: "Cuánto valdría tu vivienda reformada: para vender o para alquilar. La decisión, clara.",
    },
    {
        icon: "📄",
        title: "Informe descargable",
        description: "PDF profesional para negociar con el comprador, hablar con el banco o decidir en familia.",
    },
];

const situations = [
    { icon: "📜", title: "Herencia", description: "Has heredado una vivienda y necesitas decidir: vender, alquilar o reformar." },
    { icon: "💔", title: "Divorcio", description: "Necesitas valorar la vivienda para el acuerdo. Datos objetivos para ambas partes." },
    { icon: "🏠", title: "Mudanza", description: "Te mudas y quieres sacar el máximo de tu piso actual. Reformar puede sumar miles." },
    { icon: "💵", title: "Alquiler", description: "Quieres alquilar pero tu vivienda necesita mejoras. Te decimos si la inversión se recupera." },
];

const LandingPropietario = () => {
    useRedirectIfLoggedIn("/analizarpropiedad/nueva");
    return (
        <LandingLayout>
            <div className="max-w-5xl mx-auto px-6 pt-4">
                <Link to="/" className="text-sm text-gray-500 hover:text-brand-blue transition-colors">
                    ← Todos los perfiles
                </Link>
            </div>

            <LandingHero
                badge="Para propietarios"
                title="Entiende cuánto vale tu casa"
                highlight="y cuánto más si la reformas"
                subtitle="Descubre el valor actual de tu vivienda y cuánto podrías ganar reformándola antes de vender o alquilar. Datos de mercado de tu zona para tomar la mejor decisión."
                ctaText="Valorar mi vivienda"
                bgClass="bg-gradient-to-br from-pink-50 to-pink-100"
                highlightClass="text-pink-700"
            />

            <ProblemsSection
                title="Decidir sin datos es arriesgar tu patrimonio"
                problems={problems}
            />

            <section className="py-20 bg-white">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-brand-blue mb-4 text-center">La diferencia de reformar antes de vender</h2>
                    <p className="text-center text-gray-500 text-lg max-w-2xl mx-auto mb-12">
                        Un ejemplo real: mismo piso, dos estrategias.
                    </p>
                    <div className="flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8">
                        <div className="bg-white border border-gray-200 rounded-xl p-8 text-center flex-1 max-w-xs shadow-sm">
                            <h3 className="font-semibold text-gray-700 mb-3">Vender tal cual</h3>
                            <div className="text-4xl font-extrabold text-gray-400 mb-2">95.000€</div>
                            <p className="text-sm text-gray-500">Sin reforma. El comprador asume el coste y negocia a la baja.</p>
                        </div>
                        <div className="text-2xl font-bold text-gray-400 select-none">VS</div>
                        <div className="bg-blue-50 border-2 border-brand-blue rounded-xl p-8 text-center flex-1 max-w-xs shadow-md">
                            <h3 className="font-semibold text-brand-blue mb-3">Reformar y vender</h3>
                            <div className="text-4xl font-extrabold text-brand-blue mb-2">145.000€</div>
                            <p className="text-sm text-gray-500">Reforma de 25.000€. Revalorización neta: +25.000€.</p>
                        </div>
                    </div>
                </div>
            </section>

            <SolutionGrid
                title="Lo que Reformista te ofrece"
                solutions={solutions}
            />

            <section className="py-20 bg-gray-50">
                <div className="max-w-5xl mx-auto px-6">
                    <h2 className="text-3xl font-bold text-brand-blue mb-12 text-center">Sea cual sea tu situación</h2>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        {situations.map((s, i) => (
                            <div key={i} className="bg-white shadow rounded-lg p-6 border border-gray-200 text-center hover:shadow-lg transition-shadow">
                                <div className="text-4xl mb-3">{s.icon}</div>
                                <h3 className="font-semibold text-gray-900 mb-2">{s.title}</h3>
                                <p className="text-gray-500 text-sm">{s.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <EarlyAccessCTA />

            <FinalCTASection
                title="Tu vivienda vale más de lo que crees"
                text="Descubre cuánto con un análisis gratuito. Introduce los datos de tu vivienda y en segundos tienes la respuesta."
                ctaText="Valorar mi vivienda"
                rolNombre="propietario"
            />
        </LandingLayout>
    );
};

export default LandingPropietario;
